/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
/* global fetch */

import React from "react";
import PropTypes from "prop-types";
import get from "lodash/get";
import InfiniteStories from "./infinite-stories";
import { getInfiniteScrollApi, getInfiniteScrollConfigStyle, maySetGlobals, getPrimaryClubSection, populateLinkedStoriesFallback } from "../../utils";
import "../../../assets/stylesheets/grid.scss";
import "./story.m.css";
import { findStoryTemplate, getStoryComponent } from "../../component-bundles/bundling-utils";
import { addSlotsToStory } from "../../../utils/story-ads-from-pb";
import { getCustomSeoData } from "../../../utils/getCustomSeoData";
import { notifyStoryInView, resetHostRender, updateStoryRootZIndex } from "../video-player";

import camelCase from "lodash/camelCase";
export function StoryPageContent(data, config, renderStoryPageContent, preview, previewIndex) {
  const { story, index, relatedStories: staticRelatedStories, customAsideCollections } = data || {};
  const firstStory = (previewIndex ?? index) === 0;
  const infiniteScrollStyle = getInfiniteScrollConfigStyle(config);
  const showStoryTemplate = infiniteScrollStyle !== "read-more" || (infiniteScrollStyle === "read-more" && firstStory);
  const props = {
    story: story,
    config: config,
    preview: preview,
    firstStory: firstStory,
    staticRelatedStories: staticRelatedStories,
    customAsideCollections: customAsideCollections,
    index: index,
    showSnapshotTemplate: !showStoryTemplate && !preview,
  };
  return (typeof renderStoryPageContent === "function" && renderStoryPageContent(props)) || null;
}

const getItemsWithComponent = (items, config) => {
  const updatedItems = items.map(async (item) => {
    const storyObj = item.story || item;
    const { storyTemplate } = findStoryTemplate({ story: storyObj, config });
    const storyType = storyTemplate || "text";
    const storyField = ["text-story", "photo-story", "video-story", "liveBlog-story", "listicle-story"].includes(
      `${camelCase(storyType)}-story`
    )
      ? "story"
      : "customStory";
    // adds slots to story elements directly
    const { storyCards, customSlotAfterStory = {} } = addSlotsToStory(
      storyObj.cards,
      get(config, ["pagebuilder-config", storyField, `${camelCase(storyType)}-story`, "story-slots"], {}),
      storyObj.customSlotAfterStory
    );
    storyObj.cards = storyCards;
    storyObj.customSlotAfterStory = customSlotAfterStory;

    const enableAffiliateMarketing = get(config, ["pagebuilder-config", "affiliateMarketing", "enableAffiliateMarketing"], false);
    const effectiveTemplate = storyTemplate === "affiliate-marketing" && !enableAffiliateMarketing ? "text" : storyTemplate;
    const StoryComponent = await getStoryComponent(effectiveTemplate);

    // Safeguard: Ensure linked-stories is populated for also-read elements
    populateLinkedStoriesFallback(storyObj);

    return {
      story: storyObj,
      StoryComponent,
      customSeo: getCustomSeoData({}, "story-page", storyObj),
    };
  });
  return updatedItems;
};

function storyPageLoadItems(pageNumber, story = {}, config = {}, extraParams = {}) {
  const infiniteScrollType = get(config, ["pagebuilder-config", "infiniteScroll", "infiniteScrollType"], "");
  const collectionSlug =
    get(config, ["pagebuilder-config", "infiniteScroll", "collectionSlug"]) || "curated-story-list";

  const primaryClub = getPrimaryClubSection(story, get(config, ["sections"], []));
  const clubSectionId = primaryClub ? primaryClub.id : get(story, ["sections", 0, "id"], null);

  const rawSeenIds = extraParams.seenStoryIds || [];
  const seenStoryIds = Array.isArray(rawSeenIds) ? rawSeenIds.join(",") : rawSeenIds;

  const params = {
    pageNumber,
    sectionId: clubSectionId,
    clubSectionId,
    storyId: story.id,
    collectionSlug,
    seenStoryIds,
    limit: 2
  };

  // If infiniteScrollType is section-based or unset, default to contextual-bff
  const effectiveScrollType =
    infiniteScrollType === "section-based" || !infiniteScrollType
      ? "contextual-bff"
      : infiniteScrollType;

  return getInfiniteScrollApi(effectiveScrollType, params, config, story)
    .then((url) => fetch(url))
    .then((response) => response.json())
    .then(async (response) => {
      let rawItems = [];
      if (response?.data?.items?.length) {
        rawItems = response.data.items.filter(
          (item) => item.type === "story" && (item.id || item.story?.id) !== story.id
        );
      } else {
        const itemsList = response?.stories || response?.items || [];
        rawItems = itemsList.filter((item) => (item.id || item.story?.id) !== story.id);
      }
      return await Promise.all(getItemsWithComponent(rawItems, config));
    })
    .catch((err) => {
      console.error("Error in retrieving infinite scroll stories :", err);
      return [];
    });
}

export class StoryPage extends React.Component {
  doAnalytics(story) {
    const { registerPageView } = require("@quintype/framework/client/analytics");
    registerPageView({ pageType: "story-page", data: { story } }, `/${story.slug}`);

    if (typeof window !== "undefined") {
      const getPageData = (storyKey = "", storyValue = "") => get(story, [storyKey], []).map((item) => item[storyValue]);
      const primarySection = get(getPageData("sections", "slug"), [0]) || null;
      const sections = getPageData("sections", "slug").join(",") || null;
      const authors = getPageData("authors", "name").join(",") || null;

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "pageview",
        author: authors || null,
        category: primarySection || null,
        sections: sections || null,
        context: "Story Page",
      });
    }
  }

  onStoryFocus(item) {
    const { story } = item;
    console.log(`Story In View: ${item.story.headline}`);
    const getPageData = (storyKey = "", storyValue = "") => get(story, [storyKey], []).map((item) => item[storyValue]);
    const pageData = {
      "page-type": "story-page",
      "story-slug": get(story, ["slug"], null),
      "story-id": get(story, ["id"], null),
      "story-type": get(story, ["story-template"], null),
      "primary-section": get(getPageData("sections", "slug"), [0]) || null,
      sections: getPageData("sections", "slug").join(",") || null,
      authors: getPageData("authors", "name").join(",") || null,
      "sections-name": getPageData("sections", "name").join(",") || null,
      "sections-display-name": getPageData("sections", "display-name").join(",") || null,
      "authors-id": getPageData("authors", "id").join(",") || null,
    };
    maySetGlobals(pageData);

    if (typeof window !== "undefined") {
      notifyStoryInView(get(story, ["slug"], null) || get(story, ["id"], null));

      // Dispatch Minute Media event for active story in infinite scroll
      const articleId = get(story, ["id"], null);
      if (articleId) {
        window.dispatchEvent(
          new CustomEvent("finiteScrollEvent", {
            detail: { articleId }
          })
        );
      }
    }
  }

  componentWillUnmount() {
    if (typeof window !== "undefined") {
      resetHostRender();
      updateStoryRootZIndex(null);
    }
  }

  render() {
    const infiniteScrollType = get(
      this.props.config,
      ["pagebuilder-config", "infiniteScroll", "infiniteScrollType"],
      ""
    );
    const firstStory = get(this.props, ["data", "index"], 0) === 0;
    const infiniteScrollStyle = getInfiniteScrollConfigStyle(this.props.config);
    const showStoryTemplate =
      infiniteScrollStyle !== "read-more" || (infiniteScrollStyle === "read-more" && firstStory);
    if (this.props.preview || infiniteScrollType === "disabled") {
      return StoryPageContent(
        this.props.data,
        this.props.config,
        this.props.renderStoryPageContent,
        this.props.preview
      );
    }
    return (
      <div className="infinite-wrapper">
        {!showStoryTemplate && !this.props.preview ? (
          <InfiniteStories
            {...this.props}
            renderStoryPageContent={this.props.renderStoryPageContent}
            loadItems={storyPageLoadItems}
            onItemFocus={this.onStoryFocus}
            onInitialItemFocus={(item) => this.doAnalytics(item.story)}
            doNotChangeUrl={true}
            neverHideItem={true}
          />
        ) : (
          <InfiniteStories
            {...this.props}
            renderStoryPageContent={this.props.renderStoryPageContent}
            loadItems={storyPageLoadItems}
            onItemFocus={this.onStoryFocus}
            onInitialItemFocus={(item) => this.doAnalytics(item.story)}
            neverHideItem={true}
          />
        )}
      </div>
    );
  }
}

StoryPageContent.propTypes = {
  config: PropTypes.object,
  data: PropTypes.shape({
    story: PropTypes.object,
    index: PropTypes.number,
    relatedStories: PropTypes.array,
    preview: PropTypes.bool,
    customAsideCollections: PropTypes.object,
    staticRelatedStories: PropTypes.array,
  }),
};

StoryPage.propTypes = {
  preview: PropTypes.bool,
  config: PropTypes.object,
  renderStoryPageContent: PropTypes.any,
  data: PropTypes.shape({
    story: PropTypes.object,
    index: PropTypes.number,
    relatedStories: PropTypes.array,
    preview: PropTypes.bool,
    customAsideCollections: PropTypes.object,
    staticRelatedStories: PropTypes.array,
  }),
};
